import { useEffect, useRef } from "react";
import { getDatabase, ref, onChildChanged, get, update } from "firebase/database";
import { audioAlertService } from "../Utils/audioService";
// Assuming there is an alert sound, using the one from the Escort module
import stoppedSound from "../Raw/stopped.mp3"; 

// 10 minutes MVS timer in milliseconds
const MVS_TIMEOUT = 10 * 60 * 1000;

export const useEscortMonitor = () => {
  // Store states to detect transitions
  const stateRef = useRef({});
  // Store active MVS timers
  const timersRef = useRef({});
  const initialized = useRef(false);
  const lastTransitionTimeRef = useRef({});

  useEffect(() => {
    const db = getDatabase();
    const usersRef = ref(db, "users");

    // 1. Fetch initial state so we don't trigger alerts on first load
    get(usersRef).then((snapshot) => {
      if (snapshot.exists()) {
        const users = snapshot.val();
        Object.keys(users).forEach((userId) => {
          const user = users[userId];
          const isMoving = user?.is_moving ?? user?.IsMoving;
          const stoppedAt = user?.stoppedAt || (isMoving === false ? (Number(user?.currentTimeStamp) || Date.now()) : null);

          const rawTs = Number(user?.currentTimeStamp || 0);
          const isUserOnline = Boolean(
            user?.socketId ||
            user?.online_status ||
            user?.isOnline ||
            (rawTs > 0 && Date.now() - rawTs < 60000)
          );

          stateRef.current[userId] = {
            isMoving: isMoving !== undefined ? Boolean(isMoving) : false,
            isOnline: isUserOnline,
            wellbeing: user?.Wellbeing_Response || user?.wellbeingResponse,
            userFBK: String(user?.userFBK || "").trim(),
            stoppedAt,
          };
        });
      }
      initialized.current = true;
    }).catch(err => console.error("Error fetching initial users for monitor:", err));

    // 2. Listen to changes
    const handleUserUpdate = (userSnapshot) => {
      if (!initialized.current) return; // Ignore updates while initializing

      const userId = userSnapshot.key;
      const user = userSnapshot.val();
      if (!user) return;

      const userName = user?.name || user?.userName || user?.fullName || "User";
      const rawIsMoving = user?.is_moving ?? user?.IsMoving;
      const isMoving = rawIsMoving !== undefined ? Boolean(rawIsMoving) : undefined;
      const rawTs = Number(user?.currentTimeStamp || 0);
      const isOnline = Boolean(
        user?.socketId ||
        user?.online_status ||
        user?.isOnline ||
        (rawTs > 0 && Date.now() - rawTs < 60000)
      );
      const wellbeing = user?.Wellbeing_Response || user?.wellbeingResponse;
      const rawFBK = String(user?.userFBK || "").trim();

      const tripStatus = String(user?.Trip_Status || user?.tripStatus || user?.status || "").toLowerCase();
      const isTripActive = !["completed", "canceled", "cancelled", "ended", "rejected", "failed", "resolved"].includes(tripStatus);

      // Clean up timer if trip is no longer active
      if (!isTripActive && timersRef.current[userId]) {
        clearTimeout(timersRef.current[userId]);
        delete timersRef.current[userId];
      }

      const prev = stateRef.current[userId] || {};
      const now = Date.now();
      const lastTransition = lastTransitionTimeRef.current[userId] || 0;

      // When an active trip ends, immediately stop voice and alert listeners
      if (prev.isTripActive && !isTripActive) {
        audioAlertService.stop();
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("stopEscortVoice", {
              detail: { userId, tripStatus },
            })
          );
        }
      }

      // 1. Movement Status Transition
      if (isTripActive && isMoving !== undefined && prev.isMoving !== undefined) {
        if (isMoving === false && prev.isMoving === true && (now - lastTransition > 12000)) {
          lastTransitionTimeRef.current[userId] = now;
          // Just transitioned from moving to stopped
          audioAlertService.playAlert(`${userName} has stopped moving.`, stoppedSound);

          // Write stoppedAt only if not already recently set
          if (!user?.stoppedAt || (now - Number(user.stoppedAt) > 60000)) {
            try {
              update(ref(db, `users/${userId}`), { stoppedAt: now }).catch(() => {});
            } catch (_) {}
          }
          
          // Start MVS timer (10m)
          if (timersRef.current[userId]) clearTimeout(timersRef.current[userId]);
          timersRef.current[userId] = setTimeout(() => {
            audioAlertService.playAlert(`${userName} has remained stopped for ten minutes. Wellbeing check required.`, stoppedSound);
          }, MVS_TIMEOUT);

        } else if (isMoving === true && prev.isMoving === false && (now - lastTransition > 12000)) {
          lastTransitionTimeRef.current[userId] = now;
          // Started moving again: CLEAR old timer and announce moving
          audioAlertService.playAlert(`${userName} is moving again.`);
          if (timersRef.current[userId]) {
            clearTimeout(timersRef.current[userId]);
            delete timersRef.current[userId];
          }

          if (user?.stoppedAt) {
            try {
              update(ref(db, `users/${userId}`), { stoppedAt: null, lastMovedAt: now }).catch(() => {});
            } catch (_) {}
          }
        }
      }

      // 2. Connection Status
      if (isTripActive && prev.isOnline !== undefined && !isOnline && prev.isOnline) {
         audioAlertService.playAlert(`${userName}’s phone is offline.`);
      }

      // 3. Wellbeing Status & User Response Voice Alerts
      if (isTripActive) {
        const prevFBK = prev.userFBK || "";
        const hasNewFeedback =
          rawFBK &&
          rawFBK !== prevFBK &&
          rawFBK !== "No Feedback" &&
          rawFBK !== "null" &&
          rawFBK !== "undefined" &&
          rawFBK !== "false";

        if (hasNewFeedback) {
          if (rawFBK === "I am in Danger") {
            audioAlertService.playAlert(`Emergency alert! ${userName} reported in danger!`);
          } else if (rawFBK === "Identity Verified") {
            audioAlertService.playAlert(`${userName} has verified identity.`);
          } else if (rawFBK === "Traffic Issue") {
            audioAlertService.playAlert(`${userName} reported traffic issue.`);
          } else if (rawFBK === "I am Safe") {
            audioAlertService.playAlert(`Wellbeing response: ${userName} is safe.`);
          } else {
            audioAlertService.playAlert(`${userName} response: ${rawFBK}`);
          }
        } else if ((wellbeing === "Verified" || wellbeing === "Responded") && prev.wellbeing !== wellbeing && !hasNewFeedback) {
          audioAlertService.playAlert(`Wellbeing response: ${userName} has responded.`);
        } else if (wellbeing === "Danger" && prev.wellbeing !== "Danger") {
          audioAlertService.playAlert(`Emergency alert! ${userName} reported in danger!`);
        } else if (wellbeing === "No Response" && prev.wellbeing !== "No Response") {
          audioAlertService.playAlert(`${userName} has not responded to wellbeing check.`);
        }
      }

      // Update ref
      stateRef.current[userId] = {
        isTripActive,
        isMoving: isMoving !== undefined ? isMoving : prev.isMoving,
        isOnline: isOnline !== undefined ? isOnline : prev.isOnline,
        wellbeing: wellbeing !== undefined ? wellbeing : prev.wellbeing,
        userFBK: rawFBK || prev.userFBK,
      };
    };

    const unsubscribeChanged = onChildChanged(usersRef, handleUserUpdate);

    return () => {
      unsubscribeChanged();
      // Clean up timers
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);
};
